/*
 * rheincool — Anlagen-Konfigurator (Konfigurator-Handoff.md)
 *
 * Räume statt Produkte: Anzahl Innengeräte (1–5) + Leistung je Raum.
 * Matching gegen die inline gerenderten Multisplit-Anlagen:
 *   anzahl_innengeraete ≥ Auswahl UND aussengeraet_kw ≥ Summe × Toleranzfaktor
 *   UND (falls kombinierbare_leistungen gepflegt) jede Raum-kW enthalten.
 * Trefferzahl + Ab-Preis erscheinen VOR dem Klick (aria-live). Null-Treffer
 * zeigt den nächstgelegenen Vorschlag + Beratungs-CTA — nie Sackgasse.
 * Konfiguration reist über URL-Parameter (?innengeraete=3&kw=3,5;2,5;2,0)
 * und sessionStorage; Aufklapp-Zustand in localStorage (SPEICHER).
 */
const SPEICHER = 'rc_configurator_open';

class RcConfigurator extends HTMLElement {
  connectedCallback() {
    try {
      this.stufen = JSON.parse(this.querySelector('[data-config-stufen]').textContent);
      this.anlagen = JSON.parse(this.querySelector('[data-config-anlagen]').textContent);
      this.strings = JSON.parse(this.querySelector('[data-config-strings]').textContent);
    } catch {
      return;
    }
    if (!this.stufen.length) return;

    this.details = this.closest('details');
    this.ort = this.details?.dataset.ort || 'collection';
    this.maxUnits = parseInt(this.dataset.maxUnits, 10) || 5;
    this.tolerance = parseFloat(this.dataset.tolerance) || 0.7;
    this.onTarget = this.dataset.onTarget === 'true';

    this.unitsBox = this.querySelector('[data-units]');
    this.roomsBox = this.querySelector('[data-rooms]');
    this.sumEl = this.querySelector('[data-sum]');
    this.pillEl = this.querySelector('[data-pill]');
    this.suggestionEl = this.querySelector('[data-suggestion]');
    this.contextEl = this.querySelector('[data-context]');
    this.cta = this.querySelector('[data-cta]');
    this.adviceCta = this.querySelector('[data-cta-advice]');

    this.state = this.restoreState();
    this.active = new URLSearchParams(location.search).has('innengeraete');
    this.gridSnapshot = null;
    this.renderUnits();
    this.renderRooms();
    this.update();

    // Auf der Ergebnisseite die Liste tatsaechlich einschraenken. Bisher
    // reisten die Parameter zwar mit, das Raster zeigte aber weiter die
    // komplette Collection — wer vier Fragen beantwortet hatte, bekam die
    // ungefilterte Liste.
    if (this.active) this.filtereRaster();

    this.setupDetails();
    const reset = this.details?.querySelector('[data-config-reset]');
    if (reset) {
      reset.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.resetState();
      });
    }
    this.cta.addEventListener('click', (event) => this.onApply(event));
  }

  /* ---------- Zustand ---------- */

  defaultState() {
    const kw = this.stufen[1] ? this.stufen[1].kw : this.stufen[0].kw;
    return { n: 3, rooms: [kw, kw, kw] };
  }

  restoreState() {
    // 1. URL-Parameter, 2. sessionStorage, 3. Default (3 Räume à 2,5 kW)
    const params = new URLSearchParams(location.search);
    if (params.has('innengeraete')) {
      const n = Math.min(this.maxUnits, Math.max(1, parseInt(params.get('innengeraete'), 10) || 3));
      const kws = (params.get('kw') || '')
        .split(';')
        .map((v) => parseFloat(v.replace(',', '.')))
        .filter((v) => !Number.isNaN(v));
      const rooms = Array.from({ length: n }, (_, i) => kws[i] ?? this.stufen[1]?.kw ?? this.stufen[0].kw);
      return { n, rooms };
    }
    try {
      const saved = JSON.parse(sessionStorage.getItem('rc-configurator'));
      if (saved && saved.n && Array.isArray(saved.rooms)) return saved;
    } catch {
      /* leerer Zustand */
    }
    return this.defaultState();
  }

  persist() {
    sessionStorage.setItem('rc-configurator', JSON.stringify(this.state));
  }

  resetState() {
    this.state = this.defaultState();
    sessionStorage.removeItem('rc-configurator');
    const params = new URLSearchParams(location.search);
    if (params.has('innengeraete')) {
      params.delete('innengeraete');
      params.delete('kw');
      const qs = params.toString();
      history.replaceState(null, '', location.pathname + (qs ? `?${qs}` : ''));
    }
    this.active = false;
    this.restoreGrid();
    this.renderUnits();
    this.renderRooms();
    this.update();
    if (window.rcTrack) window.rcTrack('configurator_reset', { ort: this.ort });
  }

  /* ---------- Aufklapp-Zustand ---------- */

  setupDetails() {
    if (!this.details) return;
    // Neuer Schluessel: unter dem alten steht bei Besuchern, die einmal auf
    // einer Ergebnisseite waren, ein "true", das sie nie selbst gewaehlt
    // haben — der Konfigurator klappte dadurch ueberall auf. Der alte Wert
    // wird verworfen, jeder faengt wieder zugeklappt an.
    localStorage.removeItem('configurator_open');
    const saved = localStorage.getItem(SPEICHER);
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (isMobile || this.active) {
      // Zu bleibt der Konfigurator in zwei Faellen: mobil, wo das aufgeklappte
      // Formular die ganze erste Bildschirmseite fuellt — und auf der
      // Ergebnisseite, wo der Besucher seine Auswahl schon getroffen hat und
      // die Anlagen sehen will. Die gewaehlte Konfiguration steht in der
      // Kopfzeile, aufklappen kann er jederzeit selbst.
      this.details.open = false;
    } else if (saved === 'true') this.details.open = true;
    else if (saved === 'false') this.details.open = false;

    // Das Setzen oben loest selbst ein toggle-Ereignis aus, und zwar
    // asynchron — ein Zeit-Flag ist dagegen nicht dicht. Stattdessen merken
    // wir den zuletzt selbst gesetzten Zustand: nur eine Abweichung davon
    // stammt vom Besucher. Ohne das schrieb der Startzustand der
    // Ergebnisseite "offen" in den Speicher, und der Konfigurator klappte
    // danach auf jeder Collection-Seite wieder auf.
    this.zuletztGesetzt = this.details.open;
    this.details.addEventListener('toggle', () => {
      if (this.details.open === this.zuletztGesetzt) return;
      this.zuletztGesetzt = this.details.open;
      localStorage.setItem(SPEICHER, this.details.open ? 'true' : 'false');
      if (this.details.open) {
        // Scroll-Anchoring springt sonst ans Ende des aufgeklappten Inhalts —
        // wir stellen sicher, dass der Anfang (Kopfzeile) sichtbar ist.
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        requestAnimationFrame(() => {
          this.details.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
        });
      }
      if (window.rcTrack) {
        window.rcTrack(this.details.open ? 'configurator_open' : 'configurator_collapse', { ort: this.ort });
      }
    });
  }

  /* ---------- Rendering ---------- */

  formatKw(kw) {
    return kw.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }

  renderUnits() {
    this.unitsBox.innerHTML = '';
    for (let i = 1; i <= this.maxUnits; i += 1) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rc-config__unit rc-num';
      btn.setAttribute('role', 'radio');
      btn.textContent = i;
      btn.addEventListener('click', () => this.setUnits(i));
      btn.addEventListener('keydown', (event) => this.onRadioKey(event, this.unitsBox, i - 1, (idx) => this.setUnits(idx + 1)));
      this.unitsBox.appendChild(btn);
    }
    this.syncUnits();
  }

  syncUnits() {
    [...this.unitsBox.children].forEach((btn, i) => {
      const selected = i + 1 === this.state.n;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-checked', selected ? 'true' : 'false');
      btn.tabIndex = selected ? 0 : -1;
    });
    if (this.contextEl) {
      this.contextEl.textContent = this.strings.context.replace('[n]', this.state.n);
      this.contextEl.hidden = this.state.n < 2;
    }
  }

  setUnits(n) {
    const prev = this.state.rooms;
    // Räume beim Erhöhen mit Default anlegen, beim Verringern hinten entfernen —
    // bestehende Auswahl bleibt (Handoff §3)
    const defaultKw = this.stufen[1]?.kw ?? this.stufen[0].kw;
    this.state.n = n;
    this.state.rooms = Array.from({ length: n }, (_, i) => prev[i] ?? defaultKw);
    this.syncUnits();
    this.renderRooms();
    this.update();
    if (window.rcTrack) window.rcTrack('configurator_units_select', { anzahl: n });
  }

  renderRooms() {
    this.roomsBox.innerHTML = '';
    this.state.rooms.forEach((kw, idx) => {
      const card = document.createElement('div');
      card.className = 'rc-config__room';
      const head = document.createElement('div');
      head.className = 'rc-config__room-head';
      head.innerHTML = `<b>${this.strings.room.replace('[n]', idx + 1)}</b><span>${this.strings.room_hint}</span>`;
      card.appendChild(head);

      const group = document.createElement('div');
      group.className = 'rc-config__powers';
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-label', this.strings.room_aria.replace('[n]', idx + 1));
      this.stufen.forEach((stufe, sIdx) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'rc-config__power';
        btn.setAttribute('role', 'radio');
        btn.innerHTML = `<b class="rc-num">${this.formatKw(stufe.kw)} kW</b><span>${this.strings.m2_to.replace('[m2]', stufe.m2)}</span>`;
        btn.addEventListener('click', () => this.setRoomPower(idx, stufe.kw));
        btn.addEventListener('keydown', (event) =>
          this.onRadioKey(event, group, sIdx, (next) => this.setRoomPower(idx, this.stufen[next].kw))
        );
        group.appendChild(btn);
      });
      card.appendChild(group);
      this.roomsBox.appendChild(card);
    });
    this.syncRooms();
  }

  syncRooms() {
    [...this.roomsBox.children].forEach((card, roomIdx) => {
      const buttons = card.querySelectorAll('.rc-config__power');
      buttons.forEach((btn, sIdx) => {
        const selected = this.stufen[sIdx].kw === this.state.rooms[roomIdx];
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-checked', selected ? 'true' : 'false');
        btn.tabIndex = selected ? 0 : -1;
      });
    });
  }

  setRoomPower(roomIdx, kw) {
    this.state.rooms[roomIdx] = kw;
    this.syncRooms();
    this.update();
    if (window.rcTrack) window.rcTrack('configurator_power_select', { raum: roomIdx + 1, kw });
  }

  /* WAI-ARIA-Radiogroup: Pfeiltasten wechseln */
  onRadioKey(event, group, index, select) {
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const count = group.querySelectorAll('[role="radio"]').length;
    const dir = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = (index + dir + count) % count;
    select(next);
    group.querySelectorAll('[role="radio"]')[next]?.focus();
  }

  /* ---------- Matching ---------- */

  sum() {
    return this.state.rooms.reduce((acc, kw) => acc + kw, 0);
  }

  matches(config) {
    const total = config.rooms.reduce((a, b) => a + b, 0);
    return this.anlagen.filter((a) => {
      if (a.n < config.n) return false;
      if (a.kw < total * this.tolerance) return false;
      if (a.k && a.k.length) {
        const allowed = a.k.map((v) => parseFloat(String(v).replace(',', '.')));
        if (!config.rooms.every((kw) => allowed.some((x) => Math.abs(x - kw) < 0.05))) return false;
      }
      return true;
    });
  }

  filtereRaster() {
    const raster = document.querySelector('[data-product-grid]');
    if (!raster) return;
    const treffer = this.matches(this.state);
    if (!treffer.length) return;

    const pfade = new Set(treffer.map((a) => (a.u || '').split('?')[0]));
    let sichtbar = 0;
    [...raster.children].forEach((el) => {
      const link = el.querySelector('a[href*="/products/"]');
      const pfad = link ? link.getAttribute('href').split('?')[0] : '';
      const passt = pfade.has(pfad);
      el.hidden = !passt;
      if (passt) sichtbar += 1;
    });

    // Trefferzahl mitfuehren, sonst widerspricht sie der sichtbaren Liste.
    const zaehler = document.querySelector('.rc-collection-head__count');
    if (zaehler && sichtbar > 0) {
      zaehler.textContent = this.strings.pill_hits
        ? this.strings.pill_hits.replace('[n]', sichtbar)
        : sichtbar + ' Produkte';
    }
  }

  formatMoney(value) {
    const euros = Math.floor(value);
    const grouped = new Intl.NumberFormat('de-DE').format(euros);
    return `${grouped},– €`;
  }

  update() {
    this.persist();
    const total = this.sum();
    this.sumEl.textContent = `${this.formatKw(total)} kW`;

    const hits = this.matches(this.state);
    this.pillEl.hidden = false;

    if (hits.length > 0) {
      const minPrice = Math.min(...hits.map((a) => a.p));
      let pillText = this.strings.pill_hits.replace('[n]', hits.length);
      if (this.dataset.showPrice === 'true') {
        pillText += ` · ab ${this.formatMoney(minPrice)}`;
      }
      this.pillEl.textContent = pillText;
      this.pillEl.classList.remove('rc-config__pill--none');
      this.suggestionEl.hidden = true;
      this.cta.hidden = false;
      this.adviceCta.hidden = true;
      this.cta.textContent = this.strings.cta.replace('{count}', hits.length).replace('[n]', hits.length);
    } else {
      this.pillEl.textContent = this.strings.pill_none;
      this.pillEl.classList.add('rc-config__pill--none');
      this.showSuggestion();
      this.cta.hidden = true;
      this.adviceCta.hidden = false;
      if (window.rcTrack) {
        window.rcTrack('configurator_zero_results', { anzahl: this.state.n, kw_liste: this.state.rooms.join(';') });
      }
    }

    this.cta.href = this.buildUrl();
    this.renderHits(hits);
    if (this.onTarget && this.active) this.renderResults(hits);
  }

  /* Nächstgelegener Vorschlag bei null Treffern (Handoff §7) */
  showSuggestion() {
    const candidates = this.anlagen.filter((a) => a.n >= this.state.n);
    if (!candidates.length) {
      this.suggestionEl.hidden = true;
      return;
    }
    const total = this.sum();
    const nearest = candidates.reduce((best, a) => (Math.abs(a.kw - total) < Math.abs(best.kw - total) ? a : best));
    const count = candidates.filter((a) => Math.abs(a.kw - nearest.kw) < 0.05).length;
    this.suggestionEl.textContent = this.strings.suggestion
      .replace('[kw]', this.formatKw(nearest.kw))
      .replace('[n]', count);
    this.suggestionEl.hidden = false;
  }

  buildUrl() {
    const kwList = this.state.rooms.map((kw) => this.formatKw(kw)).join(';');
    const base = this.onTarget ? location.pathname : this.dataset.targetUrl;
    const params = new URLSearchParams(this.onTarget ? location.search : '');
    params.set('innengeraete', this.state.n);
    params.set('kw', kwList);
    return `${base}?${params.toString()}`;
  }

  onApply(event) {
    if (window.rcTrack) {
      window.rcTrack('configurator_apply', {
        anzahl: this.state.n,
        kw_liste: this.state.rooms.join(';'),
        summe_kw: this.sum(),
        treffer: this.matches(this.state).length,
      });
    }
    if (this.onTarget) {
      // Auf der Ziel-Collection: URL aktualisieren + Grid ersetzen, kein Reload
      event.preventDefault();
      this.active = true;
      history.replaceState(null, '', this.buildUrl());
      this.renderResults(this.matches(this.state));
      document.querySelector('.rc-collection__grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /* Top-Treffer als Direktlinks — unabhängig von der Grid-Pagination */
  renderHits(hits) {
    const box = this.querySelector('[data-hits]');
    if (!box) return;
    if (!hits.length) {
      box.hidden = true;
      box.innerHTML = '';
      return;
    }
    const sorted = [...hits].sort((a, b) => a.p - b.p).slice(0, 8);
    box.innerHTML = '';
    sorted.forEach((a) => {
      const el = document.createElement('a');
      el.className = 'rc-config__hit';
      el.href = a.u;
      const img = a.img ? `<img src="${a.img}" alt="" loading="lazy" width="88" height="72">` : '';
      el.innerHTML = `${img}<span class="rc-config__hit-body"><span class="rc-config__hit-title">${a.t}</span><span class="rc-config__hit-price rc-num">${this.formatMoney(a.p)}</span></span>`;
      box.appendChild(el);
    });
    if (hits.length > sorted.length) {
      const more = document.createElement('a');
      more.className = 'rc-config__hits-more';
      more.href = this.buildUrl();
      more.textContent = `+ ${hits.length - sorted.length} weitere`;
      box.appendChild(more);
    }
    box.hidden = false;
  }

  /* Ziel-Collection: Grid zeigt ALLE Treffer (nicht nur Seite 1) — die
     Original-Seite wird gesichert und bei „Zurücksetzen" wiederhergestellt */
  renderResults(hits) {
    const grid = document.querySelector('.rc-collection__grid');
    if (!grid) return;
    if (this.gridSnapshot === null) this.gridSnapshot = grid.innerHTML;

    const sorted = [...hits].sort((a, b) => a.p - b.p);
    grid.innerHTML = '';
    sorted.forEach((a) => {
      const li = document.createElement('li');
      li.innerHTML = `
        <a href="${a.u}" class="rc-card rc-config-result">
          <span class="rc-card__media">${a.img ? `<img class="rc-card__image" src="${a.img}" alt="" loading="lazy">` : ''}</span>
          <span class="rc-card__body">
            <span class="rc-card__title">${a.t}</span>
            <span class="rc-card__price"><span class="rc-card__price-value rc-price rc-num">${this.formatMoney(a.p)}</span></span>
          </span>
        </a>`;
      grid.appendChild(li);
    });

    const pagination = document.querySelector('.rc-pagination');
    if (pagination) pagination.hidden = true;
    const count = document.querySelector('[data-collection-count], .rc-collection__count');
    if (count) count.textContent = this.strings.pill_hits.replace('[n]', sorted.length);
  }

  restoreGrid() {
    const grid = document.querySelector('.rc-collection__grid');
    if (grid && this.gridSnapshot !== null) {
      grid.innerHTML = this.gridSnapshot;
      this.gridSnapshot = null;
    }
    const pagination = document.querySelector('.rc-pagination');
    if (pagination) pagination.hidden = false;
  }
}

customElements.define('rc-configurator', RcConfigurator);
